/**
 * A workout put on a date, without belonging to a plan.
 *
 * The gap this fills: you know you're training on Thursday, it isn't part of
 * the programme you're running, and until now the only way to get it on the
 * calendar was to write a whole plan for it or wait until Thursday and log it
 * as you went.
 *
 * The exercises are optional, and that's the more common case: "I'm in the gym
 * Thursday" is usually decided long before what you'll do there. Saved empty,
 * it's a square that holds the day — it still starts, still gets claimed by
 * whatever you log, still counts towards adherence. Everything below the name
 * is just the option to be more specific.
 *
 * Server-side it becomes a one-day plan flagged as a one-off, so it inherits
 * everything plan days already do — the exercises pre-load when you start it,
 * the finished session claims the slot, the square turns green. It just never
 * shows up in the plan list.
 */

import { useState } from 'react'

import { measuresOf, type Exercise } from '../db'
import { unitFor, useDistanceOverrides } from '../lib/distance'
import { api, ApiError } from '../api/client'
import { displayToMetres, parseDuration, type DistancePref, type Unit } from '../lib/units'
import { ExercisePicker } from './ExercisePicker'
import { ItemRow, newItem, type DraftItem } from './PlanItemFields'

export function OneOffPlanner({
  date,
  exercises,
  unit,
  distancePref,
  onSaved,
  onCancel,
}: {
  date: string
  exercises: Exercise[]
  unit: Unit
  distancePref: DistancePref
  onSaved: () => void
  onCancel: () => void
}) {
  const overrides = useDistanceOverrides()
  const [title, setTitle] = useState('')
  const [items, setItems] = useState<DraftItem[]>([])
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const patchItem = (key: string, patch: Partial<DraftItem>) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      await api('/schedule/oneoff', {
        body: {
          date,
          title: title.trim(),
          items: items.map((i) => {
            const ms = measuresOf(i.exercise)
            return {
              exercise_id: i.exercise.id,
              target_sets: parseInt(i.sets, 10) || 3,
              target_reps: ms.includes('reps') ? parseInt(i.reps, 10) || null : null,
              target_duration_seconds: ms.includes('time') ? parseDuration(i.duration) : null,
              target_distance_m: ms.includes('distance')
                ? displayToMetres(parseFloat(i.distance),
                                  unitFor(i.exercise, { unit, pref: distancePref, overrides }))
                : null,
              target_rpe: i.rpe ? parseFloat(i.rpe) : null,
              rest_seconds: parseInt(i.rest, 10) || 120,
              notes: i.notes.trim() || null,
            }
          }),
        },
      })
      onSaved()
    } catch (err) {
      // The server names the offending exercise — "Plank is measured in time"
      // beats anything this component could work out for itself.
      setError(
        err instanceof ApiError
          ? err.message
          : "Couldn't save it — needs a connection.",
      )
    } finally {
      setBusy(false)
    }
  }

  const friendly = new Date(`${date}T12:00:00`).toLocaleDateString([], {
    weekday: 'long', month: 'short', day: 'numeric',
  })

  return (
    <div
      style={{
        marginTop: '.5rem',
        padding: '.6rem .7rem',
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius)',
      }}
    >
      <strong style={{ fontSize: '.9rem' }}>Plan {friendly}</strong>

      <label htmlFor="oneoff-title" style={{ marginTop: '.4rem', display: 'block' }}>
        Name
      </label>
      <input
        id="oneoff-title"
        value={title}
        placeholder="Arms and abs"
        onChange={(e) => setTitle(e.target.value)}
      />

      {items.map((item) => (
        <ItemRow
          key={item.key}
          item={item}
          unit={unit}
          distancePref={distancePref}
          onChange={(patch) => patchItem(item.key, patch)}
          onRemove={() => setItems((prev) => prev.filter((i) => i.key !== item.key))}
        />
      ))}

      {picking ? (
        <div style={{ marginTop: '.5rem' }}>
          <ExercisePicker
            exercises={exercises}
            onPick={(id) => {
              const exercise = exercises.find((e) => e.id === id)
              if (exercise) setItems((prev) => [...prev, newItem(exercise)])
              setPicking(false)
            }}
            onCancel={() => setPicking(false)}
          />
        </div>
      ) : (
        <button className="secondary" style={{ width: '100%' }} onClick={() => setPicking(true)}>
          Add exercise
        </button>
      )}

      {/* Exercises are optional, and saying so matters: an empty form under an
          "Add exercise" button reads as a form you haven't finished. Most
          planning is "I'm training Thursday" long before it's a list of
          lifts, and a calendar that only accepted finished prescriptions
          couldn't hold the thing people actually decide first. */}
      {items.length === 0 && (
        <p className="muted" style={{ fontSize: '.8rem', margin: '.5rem 0 0' }}>
          Exercises are optional. Save it empty to just hold the day — you can
          log whatever you end up doing when you start it.
        </p>
      )}

      {error && <div className="error" style={{ marginTop: '.5rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '.5rem', marginTop: '.5rem' }}>
        <button className="secondary" style={{ flex: 1 }} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          style={{ flex: 2 }}
          disabled={busy}
          onClick={save}
        >
          {busy ? 'Saving…'
            : items.length === 0 ? 'Just hold the day'
              : 'Put it on the calendar'}
        </button>
      </div>
    </div>
  )
}
